import React, { useState } from 'react';
import { Table, Button, Row, Col, Modal, Form, Card, Badge, Container } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaGift, FaHeart, FaShoppingBag, FaSun, FaStar } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Loader from '../../components/layout/Loader';
import Message from '../../components/layout/Message';
import { useGetEventsQuery, useDeleteEventMutation, useCreateEventMutation, useUpdateEventMutation } from '../../slices/promotionApiSlice';

const PromotionListScreen = () => {
    const { data: events, isLoading, error, refetch } = useGetEventsQuery();
    const [deleteEvent, { isLoading: loadingDelete }] = useDeleteEventMutation();
    const [createEvent, { isLoading: loadingCreate }] = useCreateEventMutation();
    const [updateEvent, { isLoading: loadingUpdate }] = useUpdateEventMutation();

    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [selectedEventId, setSelectedEventId] = useState(null);

    // Form State
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [icon, setIcon] = useState('star');
    const [gradient, setGradient] = useState('linear-gradient(135deg, #b5430f 0%, #d96e3d 100%)');
    const [buttonText, setButtonText] = useState('Ver Más');
    const [keywords, setKeywords] = useState('');
    const [isActive, setIsActive] = useState(false);
    const [startMonth, setStartMonth] = useState(1);
    const [startDay, setStartDay] = useState(1);
    const [endMonth, setEndMonth] = useState(1);
    const [endDay, setEndDay] = useState(1);
    const [link, setLink] = useState('');

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setIcon('star');
        setGradient('linear-gradient(135deg, #b5430f 0%, #d96e3d 100%)');
        setButtonText('Ver Más');
        setKeywords('');
        setIsActive(false);
        setStartMonth(1);
        setStartDay(1);
        setEndMonth(1);
        setEndDay(1);
        setLink('');
        setSelectedEventId(null);
    };

    const handleClose = () => {
        setShowModal(false);
        resetForm();
    };

    const handleShowCreate = () => {
        setModalMode('create');
        resetForm();
        setShowModal(true);
    };

    const handleShowEdit = (event) => {
        setModalMode('edit');
        setSelectedEventId(event._id);
        setTitle(event.title);
        setDescription(event.description);
        setIcon(event.icon);
        setGradient(event.gradient);
        setButtonText(event.buttonText);
        setKeywords(event.keywords ? event.keywords.join(', ') : '');
        setIsActive(event.isActive);
        
        if (event.startDate) {
            setStartMonth(event.startDate.month);
            setStartDay(event.startDate.day);
        }
        if (event.endDate) {
            setEndMonth(event.endDate.month);
            setEndDay(event.endDate.day);
        }
        
        setLink(event.link || '');
        setShowModal(true);
    };

    const deleteHandler = async (id) => {
        if (window.confirm('¿Estás seguro de eliminar este evento?')) {
            try {
                await deleteEvent(id);
                toast.success('Evento eliminado');
                refetch();
            } catch (err) {
                toast.error(err?.data?.message || err.error);
            }
        }
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        const eventData = {
            title,
            description,
            icon,
            gradient,
            buttonText,
            keywords: keywords.split(',').map((k) => k.trim()),
            isActive,
            startDate: {
                day: Number(startDay),
                month: Number(startMonth)
            },
            endDate: {
                day: Number(endDay),
                month: Number(endMonth)
            },
            link
        };

        try {
            if (modalMode === 'create') {
                await createEvent(eventData).unwrap();
                toast.success('Evento creado exitosamente');
            } else {
                await updateEvent({ _id: selectedEventId, ...eventData }).unwrap();
                toast.success('Evento actualizado exitosamente');
            }
            refetch();
            handleClose();
        } catch (err) {
            toast.error(err?.data?.message || err.error);
        }
    };

    const getIconComponent = (iconName) => {
        switch (iconName) {
            case 'gift': return <FaGift />;
            case 'heart': return <FaHeart />;
            case 'shopping-bag': return <FaShoppingBag />;
            case 'sun': return <FaSun />;
            default: return <FaStar />;
        }
    };

    const months = [
        { value: 1, label: 'Enero' },
        { value: 2, label: 'Febrero' },
        { value: 3, label: 'Marzo' },
        { value: 4, label: 'Abril' },
        { value: 5, label: 'Mayo' },
        { value: 6, label: 'Junio' },
        { value: 7, label: 'Julio' },
        { value: 8, label: 'Agosto' },
        { value: 9, label: 'Septiembre' },
        { value: 10, label: 'Octubre' },
        { value: 11, label: 'Noviembre' },
        { value: 12, label: 'Diciembre' }
    ];

    const getMonthName = (monthNumber) => {
        const month = months.find(m => m.value === monthNumber);
        return month ? month.label : '';
    };

    const formatDate = (dateObj) => {
        if (!dateObj) return '';
        return `${dateObj.day} de ${getMonthName(dateObj.month)}`;
    };

    return (
        <Container fluid className="py-4 px-md-5">
            <Row className="align-items-center mb-4">
                <Col>
                    <h2 className="fw-bold text-dark">Eventos y Promociones</h2>
                    <p className="text-muted">Gestiona las campañas estacionales de tu tienda</p>
                </Col>
                <Col className="text-end">
                    <Button 
                        variant="primary" 
                        className="d-flex align-items-center gap-2 ms-auto shadow-sm rounded-pill px-4"
                        onClick={handleShowCreate}
                        style={{ backgroundColor: '#b5430f', borderColor: '#b5430f' }}
                    >
                        <FaPlus /> <span className="d-none d-sm-inline">Crear Evento</span>
                    </Button>
                </Col>
            </Row>

            {(loadingCreate || loadingDelete || loadingUpdate) && <Loader />}

            {isLoading ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">{error?.data?.message || error.error}</Message>
            ) : (
                <>
                    {/* Desktop View */}
                    <div className="d-none d-md-block shadow-sm rounded-4 overflow-hidden bg-white">
                        <Table hover responsive className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr>
                                    <th className="py-3 text-center" style={{ width: '80px' }}>Icono</th>
                                    <th className="py-3 ps-3 text-start">Evento</th>
                                    <th className="py-3 text-center">Estado</th>
                                    <th className="py-3 text-center">Fechas</th>
                                    <th className="py-3 text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((event) => (
                                    <tr key={event._id}>
                                        <td className="text-center">
                                            <div 
                                                className="d-flex align-items-center justify-content-center rounded-circle text-white mx-auto"
                                                style={{ width: '40px', height: '40px', background: event.gradient }}
                                            >
                                                {getIconComponent(event.icon)}
                                            </div>
                                        </td>
                                        <td className="ps-3 text-start">
                                            <div style={{ maxWidth: '350px' }}>
                                                <div className="fw-bold text-truncate" title={event.title.replace('{name}', '')}>
                                                    {event.title.replace('{name}', '')}
                                                </div>
                                                <small className="text-muted d-block text-truncate" title={event.description}>
                                                    {event.description}
                                                </small>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            {event.isActive ? (
                                                <Badge bg="success" className="rounded-pill">Activo</Badge>
                                            ) : (
                                                <Badge bg="secondary" className="rounded-pill">Inactivo</Badge>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex flex-column small text-muted align-items-center">
                                                <span><FaCalendarAlt className="me-1" /> {formatDate(event.startDate)}</span>
                                                <span><FaCalendarAlt className="me-1" /> {formatDate(event.endDate)}</span>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <Button 
                                                variant="light" 
                                                className="btn-sm me-2 rounded-circle shadow-sm"
                                                onClick={() => handleShowEdit(event)}
                                            >
                                                <FaEdit className="text-primary" />
                                            </Button>
                                            <Button 
                                                variant="light" 
                                                className="btn-sm rounded-circle shadow-sm"
                                                onClick={() => deleteHandler(event._id)}
                                            >
                                                <FaTrash className="text-danger" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    {/* Mobile View */}
                    <div className="d-md-none">
                        {events.map((event) => (
                            <Card key={event._id} className="mb-3 shadow-sm border-0 rounded-4">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <div 
                                                className="d-flex align-items-center justify-content-center rounded-circle text-white"
                                                style={{ width: '48px', height: '48px', background: event.gradient }}
                                            >
                                                {getIconComponent(event.icon)}
                                            </div>
                                            <div>
                                                <h6 className="fw-bold mb-0">{event.title.replace('{name}', '')}</h6>
                                                {event.isActive ? (
                                                    <Badge bg="success" className="rounded-pill mt-1">Activo</Badge>
                                                ) : (
                                                    <Badge bg="secondary" className="rounded-pill mt-1">Inactivo</Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-muted small mb-3">{event.description}</p>
                                    <div className="d-flex justify-content-between align-items-center bg-light p-2 rounded-3 mb-3">
                                        <small className="text-muted"><FaCalendarAlt /> {formatDate(event.startDate)}</small>
                                        <small className="text-muted"><FaCalendarAlt /> {formatDate(event.endDate)}</small>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button 
                                            variant="outline-primary" 
                                            className="flex-grow-1 rounded-pill"
                                            onClick={() => handleShowEdit(event)}
                                        >
                                            <FaEdit className="me-2" /> Editar
                                        </Button>
                                        <Button 
                                            variant="outline-danger" 
                                            className="flex-grow-1 rounded-pill"
                                            onClick={() => deleteHandler(event._id)}
                                        >
                                            <FaTrash className="me-2" /> Eliminar
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                </>
            )}

            {/* Edit/Create Modal */}
            <Modal show={showModal} onHide={handleClose} centered size="lg" className="event-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">
                        {modalMode === 'create' ? 'Crear Nuevo Evento' : 'Editar Evento'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <Form onSubmit={submitHandler}>
                        <Row>
                            <Col md={12}>
                                <Form.Group controlId="title" className="mb-3">
                                    <Form.Label column='sm' className="small fw-bold text-muted">TÍTULO</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ej: ¡Feliz Navidad, {name}!"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="rounded-3 py-2"
                                        required
                                    />
                                    <Form.Text className="text-muted small">
                                        Usa <strong>{'{name}'}</strong> para insertar el nombre del usuario.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group controlId="description" className="mb-3">
                                    <Form.Label column='sm' className="small fw-bold text-muted">DESCRIPCIÓN</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="Descripción corta del evento..."
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        className="rounded-3"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="icon" className="mb-3">
                                    <Form.Label column='sm' className="small fw-bold text-muted">ICONO</Form.Label>
                                    <Form.Select
                                        value={icon}
                                        onChange={(e) => setIcon(e.target.value)}
                                        className="rounded-3 py-2"
                                    >
                                        <option value="star">⭐ Estrella</option>
                                        <option value="gift">🎁 Regalo</option>
                                        <option value="heart">❤️ Corazón</option>
                                        <option value="shopping-bag">🛍️ Bolsa</option>
                                        <option value="sun">☀️ Sol</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="gradient" className="mb-3">
                                    <Form.Label column='sm' className="small fw-bold text-muted">GRADIENTE (CSS)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="linear-gradient(...)"
                                        value={gradient}
                                        onChange={(e) => setGradient(e.target.value)}
                                        className="rounded-3 py-2"
                                    />
                                    <div className="mt-2 rounded-2" style={{ height: '10px', background: gradient }}></div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="buttonText" className="mb-3">
                                    <Form.Label column='sm' className="small fw-bold text-muted">TEXTO BOTÓN</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ej: Ver Ofertas"
                                        value={buttonText}
                                        onChange={(e) => setButtonText(e.target.value)}
                                        className="rounded-3 py-2"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="link" className="mb-3">
                                    <Form.Label column='sm' className="small fw-bold text-muted">ENLACE (Opcional)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="/search/navidad"
                                        value={link}
                                        onChange={(e) => setLink(e.target.value)}
                                        className="rounded-3 py-2"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group controlId="keywords" className="mb-3">
                            <Form.Label column='sm' className="small fw-bold text-muted">PALABRAS CLAVE</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="navidad, regalo, luces (separadas por coma)"
                                value={keywords}
                                onChange={(e) => setKeywords(e.target.value)}
                                className="rounded-3 py-2"
                            />
                        </Form.Group>

                        <Row>
                            <Col md={12}>
                                <h6 className="small fw-bold text-muted text-uppercase mb-3">Fecha de Inicio</h6>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="startMonth" className="mb-3">
                                    <Form.Label column='sm' className="small text-muted">Mes</Form.Label>
                                    <Form.Select
                                        value={startMonth}
                                        onChange={(e) => setStartMonth(e.target.value)}
                                        className="rounded-3 py-2"
                                    >
                                        {months.map(m => (
                                            <option key={m.value} value={m.value}>{m.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="startDay" className="mb-3">
                                    <Form.Label column='sm' className="small text-muted">Día</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={startDay}
                                        onChange={(e) => setStartDay(e.target.value)}
                                        className="rounded-3 py-2"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <h6 className="small fw-bold text-muted text-uppercase mb-3">Fecha de Fin</h6>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="endMonth" className="mb-3">
                                    <Form.Label column='sm' className="small text-muted">Mes</Form.Label>
                                    <Form.Select
                                        value={endMonth}
                                        onChange={(e) => setEndMonth(e.target.value)}
                                        className="rounded-3 py-2"
                                    >
                                        {months.map(m => (
                                            <option key={m.value} value={m.value}>{m.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="endDay" className="mb-3">
                                    <Form.Label column='sm' className="small text-muted">Día</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={endDay}
                                        onChange={(e) => setEndDay(e.target.value)}
                                        className="rounded-3 py-2"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group controlId="isActive" className="mb-4">
                            <div className="d-flex align-items-center p-3 border rounded-3 bg-light">
                                <Form.Check
                                    type="switch"
                                    id="custom-switch"
                                    label={isActive ? "Evento Activo" : "Evento Inactivo"}
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                    className="fw-bold"
                                />
                                <div className="ms-auto">
                                    {isActive ? <FaCheckCircle className="text-success h4 mb-0" /> : <FaTimesCircle className="text-secondary h4 mb-0" />}
                                </div>
                            </div>
                        </Form.Group>

                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="light" onClick={handleClose} className="rounded-pill px-4">
                                Cancelar
                            </Button>
                            <Button 
                                type="submit" 
                                variant="primary" 
                                className="rounded-pill px-4"
                                style={{ backgroundColor: '#b5430f', borderColor: '#b5430f' }}
                            >
                                {modalMode === 'create' ? 'Crear Evento' : 'Guardar Cambios'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default PromotionListScreen;
